import { createTheme } from '@mui/material/styles';

/* Obsidian font stack — matches evyasys-tokens.css --font-sans */
const obsidianSans = "'Schibsted Grotesk', -apple-system, 'Segoe UI', sans-serif";

const sharedComponents = {
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        fontSize: '13.5px',
        fontWeight: 500,
        fontFamily: obsidianSans,
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        '&:before': { height: 0 },
        borderRadius: '12px !important',
        border: '1px solid var(--line2)',
        boxShadow: 'none',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      containedPrimary: {
        boxShadow: 'none',
        fontFamily: obsidianSans,
        fontWeight: 600,
        '&:hover': { boxShadow: 'none' },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
        fontFamily: obsidianSans,
        fontSize: '14.5px',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--line2)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--line3)' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--txt)',
          borderWidth: '1px',
        },
        '&.Mui-focused:focus-visible': { outline: '2px solid var(--acc-dim)', outlineOffset: '2px' },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontFamily: obsidianSans,
        fontSize: '14.5px',
        color: 'var(--dim)',
        '&.Mui-focused': { color: 'var(--acc-text, var(--txt))' },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: '12px',
        border: '1px solid var(--line2)',
        boxShadow: 'var(--shadow)',
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontFamily: "'Source Serif 4', Georgia, serif",
        fontSize: '21px',
        fontWeight: 500,
        color: 'var(--txt)',
        borderBottom: '1px solid var(--line2)',
        padding: '16px 20px',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        fontFamily: obsidianSans,
        color: 'var(--txt2)',
        paddingTop: '20px !important',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        borderTop: '1px solid var(--line)',
        padding: '12px 20px',
        gap: '8px',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontFamily: obsidianSans,
        fontSize: '12px',
        borderRadius: '9999px',
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontFamily: obsidianSans,
        fontSize: '12px',
        borderRadius: '8px',
        backgroundColor: 'var(--surf3)',
        color: 'var(--txt)',
        border: '1px solid var(--line2)',
        boxShadow: 'var(--shadow)',
      },
    },
  },
};

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0A0B0D',       /* --acc in light mode */
      contrastText: '#FFFFFF', /* --acc-ink */
      light: 'rgba(10, 11, 13, 0.08)',
      dark: '#0A0B0D',
    },
    background: {
      default: '#FFFFFF',    /* --void */
      paper: '#FFFFFF',      /* --surf */
    },
    text: {
      primary: '#000000',    /* --txt */
      secondary: '#000000',  /* --txt2 */
      disabled: '#767972',   /* --faint approx */
    },
    divider: 'rgba(18, 21, 15, 0.17)',  /* --line2 */
  },
  typography: {
    fontFamily: obsidianSans,
    fontSize: 14,
  },
  shape: { borderRadius: 12 },
  components: sharedComponents,
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#FFFFFF',       /* --acc in dark mode (mono white) */
      contrastText: '#000000',
      light: '#FFFFFF',
      dark: '#F6F8F4',
    },
    background: {
      default: '#090A0C',    /* --void */
      paper: 'color-mix(in srgb, #F6F8F4 6%, #090A0C)', /* --surf */
    },
    text: {
      primary: '#F6F8F4',    /* --txt */
      secondary: 'rgba(246, 248, 244, 0.85)',
      disabled: 'rgba(246, 248, 244, 0.43)',
    },
    divider: 'rgba(246, 248, 244, 0.17)', /* --line2 */
  },
  typography: {
    fontFamily: obsidianSans,
    fontSize: 14,
  },
  shape: { borderRadius: 12 },
  components: sharedComponents,
});
